import { useCallback, useEffect, useState } from "react";
import {
  apiErrorMessage,
  type Me,
  type RaceApi,
  type RaceResultRow,
  type RaceResults,
  type RaceSummary,
} from "../api";
import {
  formatGapMs,
  formatRaceTime,
  phaseLabel,
} from "../race/format";
import { AppHeader } from "./AppHeader";

interface RaceResultsScreenProps {
  api: RaceApi;
  raceId: string;
  me: Me | null;
  nextRace: RaceSummary | null;
  onHome(): void;
  onHistory(): void;
  onProfile(): void;
  onSignIn(): void;
  onSignOut(): void;
  onOpenRace(raceId: string): void;
  onRideAgain(): void;
}

function resultName(row: RaceResultRow): string {
  return row.name || `Rider ${row.riderId}`;
}

function ResultTable({
  rows,
  testId,
  me,
  empty,
}: {
  rows: RaceResultRow[];
  testId: string;
  me: Me | null;
  empty: string;
}) {
  if (rows.length === 0) {
    return <p className="results-empty">{empty}</p>;
  }
  return (
    <div className="results-table-wrap">
      <table className="results-table">
        <thead>
          <tr>
            <th>Pos</th>
            <th>Rider</th>
            <th>Cat</th>
            <th>Time</th>
            <th>Gap</th>
            <th>Avg W</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const self = Boolean(me && row.userId && row.userId === me.user.id);
            return (
              <tr
                key={`${testId}-${row.riderId}-${row.position ?? "dnf"}`}
                className={self ? "result-row--self" : undefined}
                data-testid={testId}
              >
                <td className="results-pos">
                  {row.status === "dnf" ? "DNF" : (row.position ?? "—")}
                </td>
                <td className="results-rider">
                  <span>{resultName(row)}</span>
                  {self ? <span className="results-self">YOU</span> : null}
                </td>
                <td>
                  <span className="category-chip">{row.category}</span>
                </td>
                <td className="results-time">{formatRaceTime(row.timeMs)}</td>
                <td className="results-gap">{formatGapMs(row.gapMs)}</td>
                <td className="results-power">
                  {row.avgPower === null ? "—" : `${Math.round(row.avgPower)} W`}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function RaceResultsScreen({
  api,
  raceId,
  me,
  nextRace,
  onHome,
  onHistory,
  onProfile,
  onSignIn,
  onSignOut,
  onOpenRace,
  onRideAgain,
}: RaceResultsScreenProps) {
  const [results, setResults] = useState<RaceResults | null>(null);
  const [race, setRace] = useState<RaceSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      try {
        const [nextResults, nextRace] = await Promise.all([
          api.getRaceResults(raceId, signal),
          api.getRace(raceId, signal).catch(() => null),
        ]);
        if (signal?.aborted) return;
        setResults(nextResults);
        if (nextRace) setRace(nextRace);
        setError(null);
      } catch (cause) {
        if (!signal?.aborted) setError(apiErrorMessage(cause));
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [api, raceId],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  useEffect(() => {
    if (!results || results.phase === "finished") return;
    const timer = window.setInterval(() => void load(), 5000);
    return () => window.clearInterval(timer);
  }, [load, results]);

  const provisional = results !== null && results.phase !== "finished";
  const selfName = me?.profile?.displayName ?? me?.user.name ?? null;
  const selfHuman = results?.humans.find(
    (row) =>
      Boolean(me && row.userId && row.userId === me.user.id) ||
      Boolean(selfName && row.name === selfName),
  );

  return (
    <main className="page-screen race-results-screen" data-testid="results">
      <AppHeader
        me={me}
        onHome={onHome}
        onSignIn={onSignIn}
        onSignOut={onSignOut}
        onProfile={onProfile}
        onHistory={onHistory}
        compact
      />

      <div className="results-page">
        <div className="results-page__head">
          <div>
            <p className="eyebrow">
              <span className="eyebrow__line" />
              {provisional ? "Provisional results" : "Final results"}
            </p>
            <h1 className="results-page__title">{race?.name ?? "Race results"}</h1>
            <p className="results-page__sub">
              {race
                ? `${race.routeName} · ${race.laps} ${race.laps === 1 ? "lap" : "laps"} · ${race.categories.join(" / ")}`
                : "Human results and pacers are separated. Bots never enter human standings."}
            </p>
          </div>
          <span className={`phase-chip phase-chip--${results?.phase ?? "finished"}`}>
            {phaseLabel(results?.phase ?? "finished")}
          </span>
        </div>

        {provisional ? (
          <div className="results-banner" role="status">
            The race is still running. Results refresh every five seconds.
          </div>
        ) : null}

        {loading && !results ? (
          <p className="results-empty" role="status">
            Reading the finish sheet…
          </p>
        ) : error && !results ? (
          <div className="load-error" role="alert">
            <p>{error}</p>
            <button type="button" className="text-button" onClick={() => void load()}>
              Try again
            </button>
          </div>
        ) : results ? (
          <>
            {selfHuman ? (
              <div className="results-self-card">
                <span className="panel-kicker">Your result</span>
                <strong>
                  {selfHuman.status === "dnf"
                    ? "DNF"
                    : `${selfHuman.position ?? "—"}${selfHuman.position ? ordinalSuffix(selfHuman.position) : ""}`}
                </strong>
                <span>
                  {formatRaceTime(selfHuman.timeMs)} · {formatGapMs(selfHuman.gapMs)} ·{" "}
                  {selfHuman.avgPower === null ? "—" : `${Math.round(selfHuman.avgPower)} W`}
                </span>
              </div>
            ) : null}

            <section className="results-section">
              <div className="section-head section-head--tight">
                <div>
                  <p className="panel-kicker">Humans</p>
                  <h2>Race results</h2>
                </div>
                <span className="results-count">
                  {results.humans.length}{" "}
                  {results.humans.length === 1 ? "human result" : "human results"}
                </span>
              </div>
              <ResultTable
                rows={results.humans}
                testId="result-row"
                me={me}
                empty="No human finishers yet."
              />
            </section>

            <section className="results-section">
              <div className="section-head section-head--tight">
                <div>
                  <p className="panel-kicker">Pacers</p>
                  <h2>Pacer results</h2>
                </div>
                <span className="results-count">Excluded from human standings</span>
              </div>
              <ResultTable
                rows={results.pacers}
                testId="pacer-row"
                me={null}
                empty="No pacer results for this race."
              />
            </section>
          </>
        ) : null}

        <div className="results-actions">
          {nextRace ? (
            <button
              type="button"
              className="ride-button ride-button--compact"
              onClick={() => onOpenRace(nextRace.id)}
            >
              Next race · {nextRace.name}
            </button>
          ) : (
            <button type="button" className="ride-button ride-button--compact" onClick={onRideAgain}>
              Free ride
            </button>
          )}
          <button type="button" className="text-button" onClick={onHome}>
            Back to calendar
          </button>
          {me ? (
            <button
              type="button"
              className="text-button"
              data-testid="results-history"
              onClick={onHistory}
            >
              Ride history
            </button>
          ) : null}
        </div>
      </div>
    </main>
  );
}

function ordinalSuffix(value: number): string {
  const mod100 = value % 100;
  if (mod100 >= 11 && mod100 <= 13) return "th";
  switch (value % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
}
